import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Flex, Heading, Message } from '@workwhile/ui'
import { IntFilter } from 'components/Filter/IntFilter'
import { StringFilter } from 'components/Filter/StringFilter'
import { useBlockWorkerContextValue } from '../context/BlockWorkerProvider'
import { ActionFilter } from './ActionFilter'
import {
  blockWorkerFormSchema,
  type BlockWorkerFormInput,
  type BlockWorkerFormValues,
} from '../schema'

export const BlockWorkerMain = () => {
  const {
    state: {
      mutationState: { isPending, isSuccess, isError, errorMessage, data },
    },
    actions: { submit },
  } = useBlockWorkerContextValue()

  const methods = useForm<BlockWorkerFormInput, unknown, BlockWorkerFormValues>(
    {
      resolver: zodResolver(blockWorkerFormSchema),
      mode: 'onSubmit',
      defaultValues: {
        companyId: { id: null },
        workerId: { id: null },
        action: 'block',
        reason: '',
      },
    }
  )

  const {
    handleSubmit,
    formState: { errors },
  } = methods

  const onSubmit = handleSubmit((values) =>
    submit({
      companyId: values.companyId.id,
      workerId: values.workerId.id,
      action: values.action,
      reason: values.reason,
    })
  )

  const validationError =
    errors.companyId?.id?.message ||
    errors.workerId?.id?.message ||
    errors.action?.message ||
    errors.reason?.message

  return (
    <Box margin={3}>
      <Heading level={1} mb={3}>
        Block/Unblock Worker
      </Heading>

      <FormProvider {...methods}>
        <Box as="form" onSubmit={onSubmit}>
          <Flex gap={2} alignItems="center" flexWrap="wrap" mb={3}>
            <IntFilter<BlockWorkerFormInput>
              fieldPath="companyId.id"
              label="Company"
              filterKey="companyId"
              inputLabel="Company ID"
            />
            <IntFilter<BlockWorkerFormInput>
              fieldPath="workerId.id"
              label="Worker"
              filterKey="workerId"
              inputLabel="Worker ID"
            />
            <ActionFilter />
            <StringFilter<BlockWorkerFormInput>
              fieldPath="reason"
              label="Reason"
              filterKey="reason"
              inputLabel="Reason"
            />
            <Button
              type="submit"
              variant="primary"
              loading={isPending}
              disabled={isPending}
            >
              Submit
            </Button>
          </Flex>
        </Box>
      </FormProvider>

      {validationError ? (
        <Box mt={2} maxWidth={640}>
          <Message variant="error">{validationError}</Message>
        </Box>
      ) : null}

      {isSuccess && data ? (
        <Box mt={3} maxWidth={640}>
          <Message variant="success">
            Worker {data.workerId} is now{' '}
            {data.blocked ? 'blocked' : 'unblocked'} at company {data.companyId}{' '}
            (action: {data.action}).
          </Message>
        </Box>
      ) : null}

      {isError && errorMessage ? (
        <Box mt={3} maxWidth={640}>
          <Message variant="error">{errorMessage}</Message>
        </Box>
      ) : null}
    </Box>
  )
}
